/** @jsxImportSource @emotion/react */
import React, { useState } from 'react';
import { css, useTheme } from '@emotion/react';
import type { BirdiTheme } from '@birdi/theme';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import ChevronDownSVG from '@birdi/icons/chevron-down.svg';
import ExternalLinkSVG from '@birdi/icons/external-link.svg';
import { maps as mapsAPI, stroot } from '@birdi/js-sdk';
import { useNavigate } from 'react-router';
import { posthog } from '../../lib/posthog';
import { useMetaSelector } from '../../meta/reducer';
import { FeatureFlagNames } from '@birdi/js-sdk/src/public/organisation-features';
import {
  UploadToMapModal,
  type UploadToMapModalVariant,
} from './upload-to-map-modal';
import {
  UploadMapDestinationModal,
  type UploadMapDestinationResult,
} from './upload-map-destination-modal';

interface Option {
  value: string;
  label: string;
  testId: string;
  link: string;
  externalLink: boolean;
  beta?: boolean;
  onClick: () => void;
}

const triggerStyles = css`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  font-weight: 600;
  color: white;
  background-color: #4ad294;
  border-radius: 40px;
  cursor: pointer;
  &:focus {
    outline: none;
  }
`;

const contentStyles = css`
  background-color: white;
  border-radius: 14px;
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.06),
    0 4px 8px -2px rgba(0, 0, 0, 0.08),
    0 12px 28px -6px rgba(0, 0, 0, 0.14);
  z-index: 1000;
  padding: 6px 8px;
`;

const itemStyles = (theme: BirdiTheme) => css`
  display: flex;
  align-items: center;
  margin: 2px 0;
  padding: 8px 12px;
  gap: 0.5rem;
  color: #374151;
  cursor: pointer;
  outline: none;
  border-radius: 8px;
  &:hover,
  &[data-highlighted] {
    background-color: ${theme.lightOcean};
  }
  &:focus {
    background-color: ${theme.lightOcean};
  }
`;

const separatorStyles = css`
  height: 1px;
  margin: 4px 0;
  background-color: #e5e7eb;
`;

const iconStyles = css`
  width: 20px;
  height: 20px;
  margin-left: 8px;
  margin-right: -4px;
`;

const externalLinkIconStyles = css`
  width: 16px;
  height: 16px;
  margin-left: 8px;
`;

interface CreateDropdownProps {
  folderId?: string | null;
}

export const CreateDropdown: React.FC<CreateDropdownProps> = ({ folderId }) => {
  const theme = useTheme() as BirdiTheme;
  const navigate = useNavigate();
  /** First-step choice modal: upload vs process-from-dashboard (same shell, different copy). */
  const [choiceModal, setChoiceModal] = useState<{
    variant: UploadToMapModalVariant;
  } | null>(null);
  const [destinationFlow, setDestinationFlow] = useState<
    'upload' | 'process'
  >('upload');
  const [uploadDestinationOpen, setUploadDestinationOpen] = useState(false);
  const [uploadDestinationMode, setUploadDestinationMode] = useState<
    'create' | 'existing' | null
  >(null);

  const meta = useMetaSelector();
  const mapsV2 = meta.orgFeatureFlags.includes(FeatureFlagNames.MapsV2);

  const canCreateMaps = meta.permissions.includes('workspace:map:create');

  // const onNewMap = async () => {
  //   const res = (await mapsAPI.postMap(stroot('nwmap2')))?.body;
  //   if (res && res.id) {
  //     // If we're in a folder, update the map to set the folderId
  //     if (folderId) {
  //       await mapsAPI.updateMap(stroot('k1665s'), res.id, { folderId });
  //     }
  //     navigate(`/map/next/${res.id}`);
  //   }
  // };

  const options: Option[] = [];

  if (mapsV2) {
    // options.push({
    //   value: 'Birdi 2.0 map',
    //   label: 'Birdi 2.0 map',
    //   testId: 'select-create-new-map',
    //   link: '',
    //   externalLink: false,
    //   onClick: onNewMap,
    // });
    options.push({
      value: 'Upload to a map',
      label: 'Upload to a map',
      testId: 'select-upload-to-map',
      link: '',
      externalLink: false,
      onClick: () => {
        posthog?.capture('dashboard - upload to a map clicked');
        setChoiceModal({ variant: 'upload' });
      },
    });
    options.push({
      value: 'Process a map',
      label: 'Process a map',
      testId: 'select-process-a-map',
      link: '',
      externalLink: false,
      onClick: () => {
        posthog?.capture('dashboard - process a map clicked');
        setChoiceModal({ variant: 'process' });
      },
    });
  }

  if (meta.orgFeatureFlags.includes(FeatureFlagNames.Missions)) {
    options.push({
      value: 'Original Birdi map',
      label: 'Original Birdi map',
      testId: 'select-create-mission-map',
      link: '/map/missions/plan',
      externalLink: false,
      onClick: () => null,
    });
  }

  return (
    <>
      <UploadToMapModal
        open={choiceModal !== null}
        variant={choiceModal?.variant ?? 'upload'}
        disableAddToExisting={false}
        onOpenChange={(open) => {
          if (!open) setChoiceModal(null);
        }}
        onCreateNewMap={() => {
          const variant = choiceModal?.variant ?? 'upload';
          setChoiceModal(null);
          setDestinationFlow(variant);
          setUploadDestinationMode('create');
          setUploadDestinationOpen(true);
        }}
        onAddToExistingMap={() => {
          const variant = choiceModal?.variant ?? 'upload';
          setChoiceModal(null);
          setDestinationFlow(variant);
          setUploadDestinationMode('existing');
          setUploadDestinationOpen(true);
        }}
      />
      <UploadMapDestinationModal
        open={uploadDestinationOpen}
        flowVariant={destinationFlow}
        onOpenChange={(next) => {
          setUploadDestinationOpen(next);
          if (!next) setUploadDestinationMode(null);
        }}
        mode={uploadDestinationMode}
        onBack={() => {
          setUploadDestinationOpen(false);
          setUploadDestinationMode(null);
          setChoiceModal({ variant: destinationFlow });
        }}
        onContinue={async (result: UploadMapDestinationResult) => {
          setUploadDestinationOpen(false);
          setUploadDestinationMode(null);
          try {
            if (result.kind === 'create') {
              const res = await mapsAPI.postMap(stroot('umcont1'), {
                name: result.mapName,
                organisationId: result.organisationId,
                flow: destinationFlow,
              });
              const mapId = res.body?.id;
              if (!mapId) return;
              if (result.folderId) {
                await mapsAPI.updateMap(stroot('umcont2'), mapId, {
                  folderId: result.folderId,
                });
              }
              const uploadQuery =
                destinationFlow === 'process'
                  ? '?upload=combined&afterUpload=processing'
                  : '?upload=combined';
              navigate(`/map/next/${mapId}${uploadQuery}`);
              return;
            }
            if (
              destinationFlow === 'process' &&
              result.kind === 'existing' &&
              result.layerFolderId
            ) {
              navigate(
                `/map/next/${result.mapId}?requestProcessing=${encodeURIComponent(result.layerFolderId)}`,
              );
              return;
            }
            navigate(`/map/next/${result.mapId}?upload=combined`);
          } catch (e) {
            console.error(e);
          }
        }}
      />
      <DropdownMenu.Root modal={false}>
        {canCreateMaps && (
          <DropdownMenu.Trigger asChild>
            <button css={triggerStyles} data-testid="dashboard-create-button">
              <span>Create</span>
              <ChevronDownSVG css={iconStyles} aria-hidden="true" />
            </button>
          </DropdownMenu.Trigger>
        )}

        <DropdownMenu.Portal>
          <DropdownMenu.Content css={contentStyles} align="end" sideOffset={5}>
            {options.map((option) => (
              <React.Fragment key={option.value}>
                <DropdownMenu.Item
                  css={itemStyles(theme)}
                  data-testid={option.testId}
                  onSelect={() => {
                    if (option.link) {
                      if (option.externalLink) {
                        window.open(option.link, '_blank');
                      } else {
                        window.location.href = option.link;
                      }
                    }
                    option.onClick();
                  }}
                >
                  {option.label}
                  {option.externalLink && (
                    <ExternalLinkSVG
                      css={externalLinkIconStyles}
                      aria-hidden="true"
                    />
                  )}
                </DropdownMenu.Item>
              </React.Fragment>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </>
  );
};
